import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import toast from 'react-hot-toast';
import {
  Box, Typography, Card, CardContent, Grid, CircularProgress, Chip, Button,
  Pagination, Divider, Stack, ToggleButton, ToggleButtonGroup,
} from '@mui/material';
import {
  MonitorHeart as BpIcon, Favorite as PulseIcon, Thermostat as TempIcon,
  Scale as WeightIcon, Air as RespIcon, Height as HeightIcon, Insights as TrendIcon,
  PictureAsPdf as PdfIcon, Description as VisitIcon, Assignment as ReportIcon,
} from '@mui/icons-material';
import { reportKindLabel, openFile as openReportFile } from './Reports';
import { formatTemp, tempUnit } from '../utils/temperature';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

const DATES_PER_PAGE = 5;

function fmtDay(iso) {
  if (!iso) return 'Undated';
  const d = new Date(iso.length <= 10 ? `${iso}T00:00:00` : iso);
  if (Number.isNaN(d.getTime())) return 'Undated';
  return d.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' });
}

/** The date an event belongs under: what happened, or what is going to happen. */
function dayKey(iso) {
  if (!iso) return '';
  return iso.length <= 10 ? iso : iso.slice(0, 10);
}

/** One flat, date-sorted history from the two things the portal knows about. */
function buildTimeline({ visits, readings, reports = [] }) {
  const events = [];

  reports.forEach((r) => {
    events.push({
      id: `report-${r.id}`,
      kind: 'report',
      // Undated report linked to a visit: file it under that visit, not the upload day.
      date: dayKey(r.report_date || r.visit_date || r.created_at),
      title: r.title || 'Report',
      reportId: r.id,
      lines: [
        [reportKindLabel(r), r.clinic_name].filter(Boolean).join(' · '),
        r.uploaded_by_role === 'patient' ? 'Added by you' : `Added by ${r.uploaded_by_name || 'your clinic'}`,
      ],
    });
  });

  visits.forEach((v) => {
    events.push({
      id: `visit-${v.visit_id}`,
      kind: 'visit',
      date: dayKey(v.date),
      title: v.reason || 'Visit',
      visitId: v.visit_id,
      lines: [v.doctor_name && `Seen by ${v.doctor_name}`].filter(Boolean),
    });
  });

  readings.forEach((r, i) => {
    const parts = [
      (r.bp_systolic || r.bp_diastolic) && `BP ${r.bp_systolic ?? '—'}/${r.bp_diastolic ?? '—'} mmHg`,
      r.pulse && `Pulse ${r.pulse} bpm`,
      r.temperature && `Temp ${formatTemp(r.temperature)}`,
      r.weight_kg && `Weight ${r.weight_kg} kg`,
      r.height_cm && `Height ${r.height_cm} cm`,
      r.respiratory_rate && `Resp ${r.respiratory_rate}/min`,
    ].filter(Boolean);
    if (!parts.length) return;
    events.push({
      id: `vitals-${r.recorded_at || i}`,
      kind: 'vitals',
      date: dayKey(r.recorded_at),
      title: 'Vitals recorded',
      lines: [parts.join(' · ')],
    });
  });

  // Newest first; undated events sink to the bottom rather than the top.
  events.sort((a, b) => (b.date || '').localeCompare(a.date || ''));

  const groups = [];
  events.forEach((e) => {
    const last = groups[groups.length - 1];
    if (last && last.date === e.date) last.events.push(e);
    else groups.push({ date: e.date, events: [e] });
  });
  return groups;
}

const KIND_META = {
  visit: { label: 'Visit', icon: <VisitIcon fontSize="small" />, color: '#0f766e' },
  vitals: { label: 'Vitals', icon: <TrendIcon fontSize="small" />, color: '#7c3aed' },
  report: { label: 'Report', icon: <ReportIcon fontSize="small" />, color: '#b45309' },
};

// Dependency-free sparkline.
function Sparkline({ values, color = '#0f766e', width = 220, height = 44 }) {
  const pts = values.filter((v) => v != null && !Number.isNaN(v));
  if (pts.length < 2) return null;
  const min = Math.min(...pts);
  const max = Math.max(...pts);
  const span = max - min || 1;
  const stepX = width / (pts.length - 1);
  const coords = pts.map((v, i) => {
    const x = i * stepX;
    const y = height - 4 - ((v - min) / span) * (height - 8);
    return [x, y];
  });
  const path = coords.map(([x, y], i) => `${i === 0 ? 'M' : 'L'}${x.toFixed(1)},${y.toFixed(1)}`).join(' ');
  const [lx, ly] = coords[coords.length - 1];
  return (
    <svg width={width} height={height} style={{ display: 'block', maxWidth: '100%' }}>
      <path d={path} fill="none" stroke={color} strokeWidth="2" strokeLinejoin="round" strokeLinecap="round" />
      <circle cx={lx} cy={ly} r="3" fill={color} />
    </svg>
  );
}

function MetricCard({ icon, title, value, unit, color, series }) {
  const has = value !== null && value !== undefined && value !== '';
  return (
    <Card sx={{ height: '100%', background: 'linear-gradient(135deg, #fffefb 0%, #fff7ed 100%)', border: '1px solid rgba(180, 83, 9, 0.12)' }}>
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
          <Box sx={{ color }}>{icon}</Box>
          <Typography variant="subtitle2" fontWeight={700} color="text.secondary">{title}</Typography>
        </Box>
        {has ? (
          <Typography variant="h5" fontWeight={800} sx={{ color: '#431407' }}>
            {value}{unit && <Typography component="span" variant="body2" color="text.secondary" sx={{ ml: 0.5 }}>{unit}</Typography>}
          </Typography>
        ) : (
          <Typography variant="body2" color="text.disabled">No reading yet</Typography>
        )}
        {series && series.length >= 2 && (
          <Box sx={{ mt: 1 }}><Sparkline values={series} color={color} /></Box>
        )}
      </CardContent>
    </Card>
  );
}

export default function Health() {
  const navigate = useNavigate();
  const [data, setData] = useState({ latest: null, readings: [] });
  const [visits, setVisits] = useState([]);
  const [reports, setReports] = useState([]);
  const [profile, setProfile] = useState(null);
  const [filter, setFilter] = useState('all');
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // The record is worth showing even if one of these is unavailable, so each
    // failure is absorbed on its own rather than blanking the page.
    const get = (path) => axios.get(`${API_URL}/api/v1/patient/${path}`).then((r) => r.data).catch(() => null);
    const reportsP = axios.get(`${API_URL}/api/v1/reports/mine`).then((r) => r.data).catch(() => null);
    Promise.all([get('vitals'), get('summaries/list'), get('profile'), reportsP])
      .then(([v, s, p, rep]) => {
        if (!v && !s) toast.error('Failed to load your health data');
        setData(v || { latest: null, readings: [] });
        setVisits(s?.visits || []);
        setReports(rep?.reports || []);
        setProfile(p || null);
      })
      .finally(() => setLoading(false));
  }, []);

  const { latest, readings } = data;

  const allGroups = useMemo(
    () => buildTimeline({ visits, readings, reports }),
    [visits, readings, reports],
  );

  const groups = useMemo(() => {
    if (filter === 'all') return allGroups;
    return allGroups
      .map((g) => ({ ...g, events: g.events.filter((e) => e.kind === filter) }))
      .filter((g) => g.events.length > 0);
  }, [allGroups, filter]);

  const pageCount = Math.max(1, Math.ceil(groups.length / DATES_PER_PAGE));
  const safePage = Math.min(page, pageCount);
  const pageGroups = groups.slice((safePage - 1) * DATES_PER_PAGE, safePage * DATES_PER_PAGE);
  const eventCount = groups.reduce((n, g) => n + g.events.length, 0);

  // The printed copy is the WHOLE record, not the page on screen — see the
  // print-only block at the bottom, which the browser's "Save as PDF" captures.
  const downloadPdf = () => window.print();
  const series = (key) => readings.map((r) => r[key]).filter((v) => v != null);
  const bp = latest && (latest.bp_systolic || latest.bp_diastolic)
    ? `${latest.bp_systolic ?? '—'}/${latest.bp_diastolic ?? '—'}` : null;

  if (loading) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}><CircularProgress /></Box>;
  }

  const hasAny = latest && Object.entries(latest).some(([k, v]) => !['recorded_at', 'source'].includes(k) && v != null);

  return (
    <Box sx={{ '@media print': { '& .no-print': { display: 'none !important' } } }}>
      <Box sx={{ mb: 3, display: 'flex', alignItems: 'flex-start', gap: 2, flexWrap: 'wrap' }}>
        <Box sx={{ flex: 1, minWidth: 240 }}>
          <Typography variant="h4" fontWeight={700}
            sx={{ fontFamily: '"Cormorant Garamond", Georgia, serif', color: '#431407', mb: 0.5 }}>
            My Health
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Your vitals and your full history — visits, readings and reports, by date.
          </Typography>
        </Box>
        <Button className="no-print" variant="contained" startIcon={<PdfIcon />} onClick={downloadPdf}>
          Download PDF
        </Button>
      </Box>

      {!hasAny ? (
        <Card sx={{ background: 'linear-gradient(145deg, #fffefb 0%, #faf6f0 100%)', maxWidth: 520, mx: 'auto' }}>
          <CardContent sx={{ textAlign: 'center', py: 6, px: 4 }}>
            <TrendIcon sx={{ fontSize: 52, color: 'primary.main', mb: 2, opacity: 0.85 }} />
            <Typography variant="h6" color="text.secondary" sx={{ fontFamily: '"Cormorant Garamond", serif', fontSize: '1.4rem' }}>
              No vitals on record yet. Your clinic will record them at your next visit.
            </Typography>
          </CardContent>
        </Card>
      ) : (
        <>
          {readings.length > 0 && (
            <Box sx={{ mb: 2 }}>
              <Chip size="small" label={`${readings.length} reading${readings.length === 1 ? '' : 's'} on record`} variant="outlined" />
              {latest?.recorded_at && (
                <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                  Last updated {new Date(latest.recorded_at).toLocaleDateString()}
                </Typography>
              )}
            </Box>
          )}
          <Grid container spacing={2.5}>
            <Grid item xs={12} sm={6} md={4}>
              <MetricCard icon={<BpIcon />} title="Blood pressure" value={bp} unit="mmHg" color="#dc2626"
                series={readings.map((r) => r.bp_systolic).filter((v) => v != null)} />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <MetricCard icon={<PulseIcon />} title="Pulse" value={latest?.pulse} unit="bpm" color="#db2777" series={series('pulse')} />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <MetricCard icon={<TempIcon />} title="Temperature" value={latest?.temperature} unit={latest?.temperature ? tempUnit(latest.temperature) : ''} color="#ea580c" series={series('temperature')} />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <MetricCard icon={<WeightIcon />} title="Weight" value={latest?.weight_kg} unit="kg" color="#0f766e" series={series('weight_kg')} />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <MetricCard icon={<HeightIcon />} title="Height" value={latest?.height_cm} unit="cm" color="#7c3aed" series={series('height_cm')} />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <MetricCard icon={<RespIcon />} title="Respiratory rate" value={latest?.respiratory_rate} unit="/min" color="#2563eb" series={series('respiratory_rate')} />
            </Grid>
          </Grid>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 2 }}>
            Vitals are recorded by your care team during intake and visits. Contact your clinic if anything looks incorrect.
          </Typography>
        </>
      )}

      {/* ── History, by date ─────────────────────────────────────────────── */}
      <Box className="no-print" sx={{ mt: 5 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap', mb: 2 }}>
          <Box sx={{ flex: 1, minWidth: 200 }}>
            <Typography variant="h5" fontWeight={700}
              sx={{ fontFamily: '"Cormorant Garamond", Georgia, serif', color: '#431407' }}>
              Your history
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {eventCount} entr{eventCount === 1 ? 'y' : 'ies'} across {groups.length} date{groups.length === 1 ? '' : 's'}
            </Typography>
          </Box>
          <ToggleButtonGroup
            size="small" exclusive value={filter}
            onChange={(_e, v) => { if (v) { setFilter(v); setPage(1); } }}
          >
            <ToggleButton value="all">All</ToggleButton>
            <ToggleButton value="visit">Visits</ToggleButton>
            <ToggleButton value="vitals">Vitals</ToggleButton>
            <ToggleButton value="report">Reports</ToggleButton>
          </ToggleButtonGroup>
        </Box>

        {groups.length === 0 ? (
          <Card sx={{ background: 'linear-gradient(145deg, #fffefb 0%, #faf6f0 100%)' }}>
            <CardContent sx={{ textAlign: 'center', py: 5 }}>
              <Typography variant="body2" color="text.secondary">
                Nothing here yet. Visits you attend, readings taken and reports added will appear on this timeline.
              </Typography>
            </CardContent>
          </Card>
        ) : (
          <>
            <Stack spacing={2}>
              {pageGroups.map((g) => (
                <Card key={g.date || 'undated'} sx={{
                  background: 'linear-gradient(135deg, #fffefb 0%, #fff7ed 100%)',
                  border: '1px solid rgba(180, 83, 9, 0.12)',
                }}>
                  <CardContent>
                    <Typography variant="subtitle2" fontWeight={800} sx={{ color: '#431407' }}>
                      {fmtDay(g.date)}
                    </Typography>
                    <Divider sx={{ my: 1 }} />
                    <Stack spacing={1.5}>
                      {g.events.map((e) => {
                        const meta = KIND_META[e.kind];
                        return (
                          <Box key={e.id} sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start' }}>
                            <Box sx={{ color: meta.color, mt: 0.25 }}>{meta.icon}</Box>
                            <Box sx={{ flex: 1, minWidth: 0 }}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                                <Typography variant="body2" fontWeight={700}>{e.title}</Typography>
                                <Chip size="small" label={meta.label} variant="outlined" />
                                {e.time && <Typography variant="caption" color="text.secondary">{e.time}</Typography>}
                              </Box>
                              {e.lines.map((l, i) => (
                                <Typography key={i} variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                                  {l}
                                </Typography>
                              ))}
                              {e.kind === 'report' && (
                                <Button size="small" sx={{ mt: 0.25, px: 0 }}
                                  onClick={() => openReportFile(`${API_URL}/api/v1/reports/mine/${e.reportId}/file`)}>
                                  Open report
                                </Button>
                              )}
                              {e.kind === 'visit' && (
                                <Button size="small" sx={{ mt: 0.25, px: 0 }}
                                  onClick={() => navigate(`/visit/${e.visitId}`)}>
                                  View visit summary
                                </Button>
                              )}
                            </Box>
                          </Box>
                        );
                      })}
                    </Stack>
                  </CardContent>
                </Card>
              ))}
            </Stack>
            {pageCount > 1 && (
              <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
                <Pagination count={pageCount} page={safePage} onChange={(_e, p) => setPage(p)} color="primary" />
              </Box>
            )}
          </>
        )}
      </Box>

      {/* The PDF. Hidden on screen, printed in full — every date, unpaginated and
          unfiltered, so what the patient shares is the whole record rather than
          whichever page they happened to be on. */}
      <Box sx={{ display: 'none', '@media print': { display: 'block' } }}>
        <Typography variant="h5" fontWeight={800}>Health record</Typography>
        <Typography variant="body2">
          {profile?.full_name || profile?.email || 'Patient'}
          {profile?.date_of_birth ? ` · DOB ${profile.date_of_birth}` : ''}
        </Typography>
        <Typography variant="caption">
          Generated {new Date().toLocaleString()} · ScribeLive patient portal
        </Typography>

        {latest && (
          <>
            <Typography variant="subtitle1" fontWeight={800} sx={{ mt: 2 }}>Latest vitals</Typography>
            <Typography variant="body2">
              {[
                bp && `BP ${bp} mmHg`,
                latest.pulse && `Pulse ${latest.pulse} bpm`,
                latest.temperature && `Temp ${formatTemp(latest.temperature)}`,
                latest.weight_kg && `Weight ${latest.weight_kg} kg`,
                latest.height_cm && `Height ${latest.height_cm} cm`,
                latest.respiratory_rate && `Resp ${latest.respiratory_rate}/min`,
              ].filter(Boolean).join(' · ') || 'No readings on record.'}
            </Typography>
          </>
        )}

        <Typography variant="subtitle1" fontWeight={800} sx={{ mt: 2 }}>
          History ({allGroups.reduce((n, g) => n + g.events.length, 0)} entries)
        </Typography>
        {allGroups.map((g) => (
          <Box key={`print-${g.date || 'undated'}`} sx={{ mt: 1.5, pageBreakInside: 'avoid' }}>
            <Typography variant="subtitle2" fontWeight={800}>{fmtDay(g.date)}</Typography>
            {g.events.map((e) => (
              <Box key={`print-${e.id}`} sx={{ pl: 1.5, mt: 0.5 }}>
                <Typography variant="body2" fontWeight={700}>
                  {KIND_META[e.kind].label}: {e.title}
                  {e.time ? ` — ${e.time}` : ''}
                </Typography>
                {e.lines.map((l, i) => (
                  <Typography key={i} variant="caption" sx={{ display: 'block' }}>{l}</Typography>
                ))}
              </Box>
            ))}
          </Box>
        ))}
      </Box>
    </Box>
  );
}
